import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Container, 
  Paper, 
  Typography, 
  Button,
  List,
  ListItem,
  IconButton,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip
} from '@mui/material';
import { 
  ThumbUp as ThumbUpIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PushPin as PushPinIcon,
  ThumbUpAlt as ThumbUpOutlinedIcon,
  ThumbDownAlt as ThumbDownIcon,
  ThumbDownOffAlt as ThumbDownOutlinedIcon,
} from '@mui/icons-material';
import api from '../../utils/api';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import ListTool from '@editorjs/list';
import Paragraph from '@editorjs/paragraph';
import Embed from '@editorjs/embed';
import SimpleImage from '@editorjs/simple-image';

function Forum() {
  const editorRef = useRef(null);
  const [posts, setPosts] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [newPost, setNewPost] = useState({ content: '' });
  const [editingPost, setEditingPost] = useState(null);
  const { user } = useSelector(state => state.auth);

  useEffect(() => {
    fetchPosts();
    if (openDialog || editingPost) {
      initEditor();
    }
    return () => {
      if (editorRef.current) {
        editorRef.current.destroy();
        editorRef.current = null;
      }
    };
  }, [openDialog, editingPost]);

  const fetchPosts = async () => {
    try {
      const response = await api.get('/forum/posts/');
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  const handleCreatePost = async () => {
    try {
      await api.post('/forum/posts/', { 
        title: 'Post',
        content: newPost.content 
      });
      setOpenDialog(false);
      setNewPost({ content: '' });
      fetchPosts();
    } catch (error) {
      console.error('Error creating post:', error);
    }
  };

  const handleEditPost = async () => {
    try {
      await api.put(`/forum/posts/${editingPost.id}/`, {
        title: 'Post',
        content: editingPost.content
      });
      setEditingPost(null);
      fetchPosts();
    } catch (error) {
      console.error('Error updating post:', error);
    }
  };

  const handleDeletePost = async (postId) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await api.delete(`/forum/posts/${postId}/`);
        fetchPosts();
      } catch (error) {
        console.error('Error deleting post:', error);
      }
    }
  };

  const handleToggleLike = async (postId) => {
    try {
      await api.post(`/forum/posts/${postId}/toggle_like/`);
      fetchPosts();
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const handleToggleDislike = async (postId) => {
    try {
      await api.post(`/forum/posts/${postId}/toggle_dislike/`);
      fetchPosts();
    } catch (error) {
      console.error('Error toggling dislike:', error);
    }
  };

  const handleTogglePin = async (postId) => {
    try {
      await api.post(`/forum/posts/${postId}/toggle_pin/`);
      fetchPosts();
    } catch (error) {
      console.error('Error toggling pin:', error);
    }
  };

  const initEditor = () => {
    const content = editingPost ? editingPost.content : newPost.content;
    
    editorRef.current = new EditorJS({
      holder: 'editorjs',
      placeholder: 'Write your post here...',
      tools: {
        header: {
          class: Header,
          config: {
            placeholder: 'Enter a header',
            levels: [2, 3],
            defaultLevel: 2
          }
        },
        list: {
          class: ListTool,
          inlineToolbar: true
        },
        paragraph: {
          class: Paragraph,
          inlineToolbar: true
        },
        image: {
          class: SimpleImage,
          inlineToolbar: true
        },
        embed: {
          class: Embed,
          inlineToolbar: true,
          config: {
            services: {
              youtube: {
                regex: /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i,
                embedUrl: 'https://www.youtube.com/embed/<%= remote_id %>',
                html: '<div style="position: relative; padding-bottom: 56.25%; height: 0;"><iframe style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;" src="<%= embedUrl %>" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div>',
                height: 320,
                width: '100%'
              }
            }
          }
        }
      },
      data: content ? JSON.parse(content) : {},
      onChange: async () => {
        const content = await editorRef.current.save();
        handleEditorChange(JSON.stringify(content));
      }
    });
  };

  const handleEditorChange = async (content) => {
    if (editingPost) {
      setEditingPost({
        ...editingPost,
        content: content
      });
    } else {
      setNewPost({
        ...newPost,
        content: content
      });
    }
  };

  const renderContent = (content) => {
    try {
      if (!content) return '';
      const data = typeof content === 'string' ? JSON.parse(content) : content;
      
      return data.blocks.map(block => {
        switch (block.type) {
          case 'paragraph':
            return `<p>${block.data.text}</p>`;
          case 'header':
            return `<h${block.data.level}>${block.data.text}</h${block.data.level}>`;
          case 'list':
            const listItems = block.data.items
              .map(item => `<li>${item}</li>`)
              .join('');
            return block.data.style === 'ordered' 
              ? `<ol>${listItems}</ol>` 
              : `<ul>${listItems}</ul>`;
          case 'image':
            return `<img src="${block.data.url}" alt="${block.data.caption || ''}" style="max-width: 100%;" />`;
          case 'embed':
            if (block.data.service === 'youtube') {
              const videoId = block.data.embed || 
                            (block.data.source && block.data.source.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([^&?]+)/)?.[1]);
              
              if (videoId) {
                return `
                  <div class="embed-youtube" style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; max-width: 100%; background-color: #000;">
                    <iframe 
                      style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
                      src="https://www.youtube.com/embed/${videoId}"
                      frameborder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowfullscreen
                    ></iframe>
                  </div>
                `;
              }
            }
            return '';
          default:
            return '';
        }
      }).join('');
    } catch (error) {
      console.error('Error rendering content:', error);
      return content || '';
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h4">Forum</Typography>
          <Button 
            variant="contained" 
            onClick={() => setOpenDialog(true)}
          >
            New Post
          </Button>
        </Box>

        <List sx={{ p: 0 }}>
          {posts.map((post) => (
            <ListItem 
              key={post.id}
              sx={{ 
                mb: 2, 
                p: 0,
                display: 'block',
                bgcolor: post.is_pinned ? 'action.selected' : 'background.paper'
              }}
            >
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Avatar sx={{ mr: 2 }}>
                    {post.username[0].toUpperCase()}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {post.username} • {format(new Date(post.created_at), 'PPP')}
                      {post.is_pinned && (
                        <PushPinIcon 
                          fontSize="small" 
                          sx={{ ml: 1, transform: 'rotate(45deg)' }}
                        />
                      )}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <IconButton 
                      size="small" 
                      onClick={() => handleToggleLike(post.id)}
                      color={post.is_liked ? "primary" : "default"}
                    >
                      {post.is_liked ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
                    </IconButton>
                    <Typography variant="body2" color="text.secondary">
                      {post.likes_count}
                    </Typography>
                    
                    <IconButton 
                      size="small" 
                      onClick={() => handleToggleDislike(post.id)}
                      color={post.is_disliked ? "error" : "default"}
                    >
                      {post.is_disliked ? <ThumbDownIcon /> : <ThumbDownOutlinedIcon />}
                    </IconButton>
                    <Typography variant="body2" color="text.secondary">
                      {post.dislikes_count}
                    </Typography>
                    
                    {post.can_edit && (
                      <>
                        <IconButton size="small" onClick={() => setEditingPost(post)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          size="small"
                          color="error"
                          onClick={() => handleDeletePost(post.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                    {user?.is_superuser && (
                      <IconButton size="small" onClick={() => handleTogglePin(post.id)}>
                        <PushPinIcon color={post.is_pinned ? "primary" : "default"} />
                      </IconButton>
                    )}
                  </Stack>
                </Box>
                <Box 
                  sx={{ 
                    '& img': { maxWidth: '100%' },
                    '& p': { margin: '0.5em 0' },
                    '& h2': { fontSize: '1.3em', margin: '0.5em 0' },
                    '& h3': { fontSize: '1.1em', margin: '0.5em 0' },
                    '& ul, & ol': { margin: '0.5em 0', paddingLeft: '2em' }
                  }}
                  dangerouslySetInnerHTML={{ 
                    __html: renderContent(post.content)
                  }}
                />
              </Box>
            </ListItem>
          ))}
        </List>

        <Dialog 
          open={openDialog || !!editingPost} 
          onClose={() => {
            setOpenDialog(false);
            setEditingPost(null);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editingPost ? 'Edit Post' : 'New Post'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <div id="editorjs" />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setOpenDialog(false);
              setEditingPost(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={editingPost ? handleEditPost : handleCreatePost} 
              variant="contained"
            >
              {editingPost ? 'Save' : 'Post'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
}

export default Forum; 